"use client";

import React, { useEffect, useState } from "react";
import Header from "./Header";
import Footer from "./Footer";
import Loader from "../../Reusable/Loader/Loader";
import WhatsapChatBot from "../../WhatsapChatBot/WhatsapChatbot";
import { CartProvider } from "../../../context/CartContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/ReactToastify.css";
import { usePathname } from "next/navigation";
import { gsap } from "gsap";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, ScrollSmoother, SplitText);
}

export default function ClientLayout({ children }) {
  const pathname = usePathname();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
     if (typeof window === "undefined" || loading) return;


    gsap.registerPlugin(ScrollTrigger, ScrollSmoother, SplitText);

    ScrollTrigger.defaults({
      toggleActions: "restart pause resume pause",
      pinType: "fixed",
    });

    const ctx = gsap.context(() => {
      // 1. .animate_title
      const animateTitleElements = document.querySelectorAll(".animate_title");
      animateTitleElements.forEach((element) => {
        new SplitText(element, {
          type: "words",
          linesClass: "linechild hide",
        });

        const split = new SplitText(element, {
          type: "words",
        });

        gsap.utils.toArray(split.words).forEach((elem) => {
          ScrollTrigger.create({
            trigger: elem,
            onEnter: () => {
              gsap.fromTo(
                elem,
                {
                  xPercent: -150,
                  autoAlpha: 0,
                },
                {
                  duration: 1,
                  xPercent: 0,
                  autoAlpha: 1,
                  ease: "power4",
                  overwrite: "auto",
                }
              );
            },
            onLeave: () => {
              gsap.fromTo(
                elem,
                {
                  autoAlpha: 1,
                },
                {
                  autoAlpha: 0,
                  overwrite: "auto",
                }
              );
            },
            onEnterBack: () => {
              gsap.fromTo(
                elem,
                {
                  xPercent: 100,
                  autoAlpha: 0,
                },
                {
                  duration: 1,
                  xPercent: 0,
                  autoAlpha: 1,
                  ease: "power4",
                  overwrite: "auto",
                }
              );
            },
            onLeaveBack: () => {
              gsap.fromTo(
                elem,
                {
                  autoAlpha: 1,
                },
                {
                  autoAlpha: 0,
                  overwrite: "auto",
                }
              );
            },
          });
        });
      });

      // 2. #revealUp and .revealUp
      const revealUpElements = gsap.utils.toArray("#revealUp, .revealUp");
      revealUpElements.forEach((elem) => {
        const split = new SplitText(elem, {
          type: "lines,words,chars",
          linesClass: "split-line",
        });

        gsap.from(split.lines, {
          scrollTrigger: {
            trigger: elem,
            toggleActions: "restart pause resume reverse",
            start: "50% 70%",
          },
          duration: 0.6,
          ease: "circ.out",
          yPercent: 100,
          stagger: 0.2,
        });
      });

      // 3. .desc_animate
      const descElements = gsap.utils.toArray(".desc_animate");
      descElements.forEach((elem) => {
        const split = new SplitText(elem, {
          type: "lines,words,chars",
          linesClass: "split-line",
        });

        gsap.from(split.lines, {
          scrollTrigger: {
            trigger: elem,
            toggleActions: "restart pause resume reverse",
            start: "50% 80%",
          },
          duration: 0.6,
          autoAlpha: 0,
          ease: "circ.out",
          yPercent: 0,
          stagger: 0.2,
        });
      });

      // 4. .wrapimage
      const wrapImages = gsap.utils.toArray(".wrapimage");
      wrapImages.forEach((element) => {
        const animation = gsap.timeline({
          paused: true,
        });

        animation.to(element, {
          scale: 1.1,
          duration: 0.5,
          ease: "power1.inOut",
        });

        element.addEventListener("mouseenter", () => animation.play());
        element.addEventListener("mouseleave", () => animation.reverse());
      });

      // 5. .reveal_insta_image
      const instaContainers = document.querySelectorAll(".reveal_insta_image");
      instaContainers.forEach((container) => {
        const image = container.querySelector("img");
        if (!image) return;

        const timeline = gsap.timeline({
          scrollTrigger: {
            trigger: container,
            start: "top 120%",
            toggleActions: "play none none reverse",
          },
        });

        timeline.set(container, {
          autoAlpha: 1,
        });

        timeline.from(container, {
          yPercent: 100,
          duration: 3,
          ease: "Expo.easeOut",
        });

        timeline.from(image, {
          yPercent: -100,
          duration: 3,
          delay: -3,
          ease: "Expo.easeOut",
        });
      });

      // 6. .helicopter_reveal_image
      const heliImage = document.querySelector(".helicopter_reveal_image");
      if (heliImage) {
        ScrollTrigger.create({
          trigger: heliImage,
          toggleClass: "helicopter_image_active",
          start: "top 90%",
        });
      }

      // 7. .bg_meeting_img parallax
      const mm = gsap.matchMedia();

      mm.add("(min-width: 769px)", () => {
        gsap.to(".bg_meeting_img", {
          scrollTrigger: {
            trigger: ".bg_meeting_img",
            toggleActions: "restart pause reverse pause",
            scrub: 1,
            start: "top bottom",
            end: "center top",
          },
          backgroundPosition: "0px -500px",
          ease: "none",
        });
      });

      mm.add("(max-width: 768px)", () => {
        gsap.to(".bg_meeting_img", {
          scrollTrigger: {
            trigger: ".bg_meeting_img",
            toggleActions: "restart pause reverse pause",
            scrub: 1,
            start: "top bottom",
            end: "center top",
          },
          backgroundPosition: "0px -100px",
          ease: "none",
        });
      });

      // 8. .subhead_animate
      const subheads = document.querySelectorAll(".subhead_animate");
      subheads.forEach((subhead) => {
        const split = new SplitText(subhead, {
          type: "lines,words,chars",
          linesClass: "split-line",
        });

        gsap.from(split.lines, {
          scrollTrigger: {
            trigger: subhead,
            toggleActions: "restart pause resume reverse",
            start: "50% 70%",
          },
          duration: 0.6,
          ease: "circ.out",
          yPercent: 100,
          stagger: 0.2,
          opacity: 0,
        });
      });

      ScrollTrigger.refresh();
    });

    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 200);

    return () => {
      clearTimeout(refreshTimer);
      ctx.revert();
    };
  }, [pathname, loading]);

  const isGoKartingForm = pathname === "/go-karting-consent-form";

  const isPrivateRoute =
    pathname === "/e-card-baldevthakur" ||
    pathname === "/adventure-resorts-school-brochure" ||
    pathname === "/e-card-prateekthakur" ||
    pathname === "/prateekthakursig" ||
    pathname === "/rameshdograsig" ||
    pathname === "/go-karting-consent-form" ||
    pathname === "/thankyou" ||
    pathname.startsWith("/payment/success/") ||
    pathname.startsWith("/payment/cancel/");

  return (
    <CartProvider>
      <main key={isPrivateRoute ? "private_route" : "public_route"}>
        {!isPrivateRoute && <Header />}
        {loading && <Loader />}
        {children}
        {!isPrivateRoute && <Footer />}
        {!isGoKartingForm && <WhatsapChatBot />}
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable={false}
          pauseOnHover={false}
          theme="light"
        />
      </main>
    </CartProvider>
  );
}
